import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import adminRanksService, { UserRank, ClanRank, RefereeRank } from '../../services/adminRanksService';
import UserRanksTab from './UserRanksTab';
import ClanRanksTab from './ClanRanksTab';
import RefereeRanksTab from './RefereeRanksTab';

const Container = styled.div`
  padding: 20px;
  color: #fff;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #00d4ff;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 2px solid #00d4ff;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? '#00d4ff' : 'transparent'};
  color: ${props => props.$active ? '#000' : '#00d4ff'};
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 1px;

  &:hover {
    background: ${props => props.$active ? '#00d4ff' : 'rgba(0, 212, 255, 0.1)'};
  }
`;

const ContentContainer = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  padding: 20px;
  min-height: 500px;
`;

type ActiveTab = 'user' | 'clan' | 'referee';

const RanksManagement: React.FC = () => {
  // URL-based tabs
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as ActiveTab) || 'user';

  const setActiveTab = useCallback((tab: ActiveTab) => {
    setSearchParams({ tab }, { replace: false });
  }, [setSearchParams]);

  const [userRanks, setUserRanks] = useState<UserRank[]>([]);
  const [clanRanks, setClanRanks] = useState<ClanRank[]>([]);
  const [refereeRanks, setRefereeRanks] = useState<RefereeRank[]>([]);
  const [loading, setLoading] = useState(false);

  const loadUserRanks = async () => {
    try {
      setLoading(true);
      const ranks = await adminRanksService.getUserRanks();
      setUserRanks(ranks);
    } catch (error) {
      console.error('Error loading user ranks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClanRanks = async () => {
    try {
      setLoading(true);
      const ranks = await adminRanksService.getClanRanks();
      setClanRanks(ranks);
    } catch (error) {
      console.error('Error loading clan ranks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRefereeRanks = async () => {
    try {
      setLoading(true);
      const ranks = await adminRanksService.getRefereeRanks();
      setRefereeRanks(ranks);
    } catch (error) {
      console.error('Error loading referee ranks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    switch (activeTab) {
      case 'user':
        loadUserRanks();
        break;
      case 'clan':
        loadClanRanks();
        break;
      case 'referee':
        loadRefereeRanks();
        break;
    }
  }, [activeTab]);

  return (
    <Container>
      <Header>
        <Title>Ranks Management</Title>
      </Header>

      <TabContainer>
        <Tab $active={activeTab === 'user'} onClick={() => setActiveTab('user')}>
          User Ranks
        </Tab>
        <Tab $active={activeTab === 'clan'} onClick={() => setActiveTab('clan')}>
          Clan Ranks
        </Tab>
        <Tab $active={activeTab === 'referee'} onClick={() => setActiveTab('referee')}>
          Referee Ranks
        </Tab>
      </TabContainer>

      <ContentContainer>
        {activeTab === 'user' && (
          <UserRanksTab 
            ranks={userRanks} 
            loading={loading} 
            onRefresh={loadUserRanks}
          />
        )}
        {activeTab === 'clan' && (
          <ClanRanksTab 
            ranks={clanRanks} 
            loading={loading} 
            onRefresh={loadClanRanks}
          />
        )}
        {activeTab === 'referee' && (
          <RefereeRanksTab 
            ranks={refereeRanks} 
            loading={loading} 
            onRefresh={loadRefereeRanks}
          />
        )}
      </ContentContainer>
    </Container>
  );
};

export default RanksManagement;